import { CountryCode, Date as pbDate } from "@travello/grpc-api/js/common_pb";
import { Gender } from "@travello/grpc-api/js/user_pb";
import {
  differenceInYears,
  fromUnixTime,
  format,
  formatDistanceStrict
} from "date-fns";

import compact from "lodash/compact";
import MobileDetect from "mobile-detect";

import { DateTime } from "luxon";

/*
 * Since CountryCode enum is located in a .d.ts file it is considered "Ambient" and no JS lookup object is produced
 * https://stackoverflow.com/questions/28818849/how-do-the-different-enum-variants-work-in-typescript
 * Therefore in order to use country codes at runtime I need to create a lookup object.
 */
export const COUNTRY_CODE_KEYS: string[] = Object.keys(CountryCode); // "AX","AL"...

export const timeSince = (seconds: number, now = Date.now()): string => {
  const to = fromUnixTime(seconds);
  return `${formatDistanceStrict(to, now)} ago`;
};

export function convertToDate(epochTime: number): string {
  const formatString = "d MMM";
  return format(fromUnixTime(epochTime), formatString).toUpperCase();
}

export function convertToTime(epochTime: number): string {
  const formatString = "h:mm aaa";
  return format(fromUnixTime(epochTime), formatString);
}

export const getCurrencyText = (
  currencyCode: string,
  floatValue: number | null
) => {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currencyCode
  }).format(floatValue || 0);
};

export const formatTime = (secondsIn: number): string => {
  let minutes: number | string = Math.floor(secondsIn / 60);
  minutes = minutes >= 10 ? minutes : "0" + minutes;
  let seconds: number | string = Math.floor(secondsIn % 60);
  seconds = seconds >= 10 ? seconds : "0" + seconds;
  return minutes + ":" + seconds;
};

export const formatDuration = (durationMins: number) => {
  const to = durationMins * 60 * 1000;
  return formatDistanceStrict(to, 0);
};

export const getFlagCode = (nationality: CountryCode): string => {
  if (nationality === CountryCode.UNKNOWN_COUNTRYCODE) {
    return "";
  } else {
    return COUNTRY_CODE_KEYS[nationality];
  }
};

// Shorten strings to avoid horrible walls of text.
// Then I'll tack on ... to show there's more
export const shortenText = (text: string, newLength: number): string => {
  if (text.length > newLength) {
    return text.substr(0, newLength) + "...";
  } else {
    return text;
  }
};

// info string thing

function genGenderString(gender: Gender): string {
  let genderString = "";
  if (gender === Gender.FEMALE) {
    genderString = "Female";
  } else if (gender === Gender.MALE) {
    genderString = "Male";
  }
  return genderString;
}

function calcAge(dob: pbDate.AsObject): string {
  let y = 0;
  if (dob.year > 0 && dob.month >= 1 && dob.day > 0) {
    y = differenceInYears(
      new Date(),
      new Date(dob.year, dob.month - 1, dob.day)
    );
  }

  if (y > 0) {
    return `${y}`;
  }
  return "";
}

export function genInfoString(gender: Gender, dob: pbDate.AsObject) {
  const genderString = genGenderString(gender);
  const ageString: string = calcAge(dob);

  return compact([genderString, ageString]).join(", ");
}

export const isForeground = () => {
  const Visibility = require("visibilityjs");

  return !Visibility.hidden();
};
export const shouldHandleVisibilityChange = () => {
  const Visibility = require("visibilityjs");

  return ["visible", "hidden"].some(v => v === Visibility.state());
};

export const isIOS = () => {
  if (typeof window !== "undefined") {
    const mobileDetect = new MobileDetect(window?.navigator.userAgent || "");
    return mobileDetect.os() === "iOS";
  }
  return false;
};

export const isAndroid = () => {
  if (typeof window !== "undefined") {
    const mobileDetect = new MobileDetect(window?.navigator.userAgent || "");
    return mobileDetect.os() === "AndroidOS";
  }
  return false;
};

export const getDateTrackingProperties = (date: string) => {
  return {
    date,
    date_offset: DateTime.fromISO(date).diffNow().days
  };
};
